"use client";
import BlockHeader from "@/components/UIkit/BlockHeader";
import Image from "next/image";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useRef, useState, useEffect, ReactNode } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore from "swiper";
import { Navigation, Autoplay } from "swiper/modules";
import { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";

SwiperCore.use([Navigation, Autoplay]);

export interface SliderBlockV2Props {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  formatList: Array<{
    bgVideo?: string | { url: string };
  }>;
  color?: "violet" | "blue" | "green";
}

export default function SliderBlockVideo({
  title,
  formatList,
  id,
  subtitle,
  color = "blue",
}: SliderBlockV2Props) {
  const backgroundColorClass =
    {
      green: "custom-gradient-green-btn-h",
      blue: "custom-gradient-blue-btn-h",
      violet: "custom-gradient-violet-btn-h",
    }[color] || "custom-gradient-green";
  const backgroundProggressColorClass =
    {
      green: "custom-gradient-green",
      blue: "custom-gradient-blue",
      violet: "custom-gradient-violet",
    }[color] || "custom-gradient-green";
  const swiperRef = useRef<SwiperCore | null>(null);
  const [progress, setProgress] = useState(0);
  const [activeSlide, setActiveSlide] = useState<number | null>(null);

  // Function to calculate progress percentage
  const calculateProgress = (swiper: SwiperCore) => {
    const prevView = () => {
      if (typeof swiper.params?.slidesPerView === "number") {
        return swiper.params.slidesPerView;
      } else {
        return 1; // Default to 1 if slidesPerView is not a number
      }
    };

    if (swiper.slides && swiper.slides.length > 0) {
      const progress =
        (swiper.activeIndex / (swiper.slides.length - prevView())) * 100;
      const currentProgress = progress || 25;
      setProgress(currentProgress);
    }
  };

  const handlePrevClick = () => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  };

  const handleNextClick = () => {
    if (swiperRef.current) swiperRef.current.slideNext();
  };

  const handlePlusClick = (index: number) => {
    if (swiperRef.current) {
      if (activeSlide === index) {
        // Close content and resume autoplay
        swiperRef.current.autoplay.start();
        setActiveSlide(null);
      } else {
        // Open content and stop autoplay
        swiperRef.current.autoplay.stop();
        setActiveSlide(index);
      }
    }
  };

  useEffect(() => {
    if (swiperRef.current) {
      // Initialize the progress bar when the component first loads
      calculateProgress(swiperRef.current);
    }
  }, []);

  return (
    <div
      id={id}
      data-meta="SliderBlockVideo"
      className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-14"
    >
      <BlockHeader title={title} subtitle={subtitle} />
      <div className="flex justify-center flex-col gap-4">
        <div className="relative overflow-hidden">
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              // Initialize the progress when swiper is mounted
              calculateProgress(swiper);
            }}
            onSlideChange={() => {
              if (swiperRef.current) {
                // Update progress on slide change
                calculateProgress(swiperRef.current);
              }
            }}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              640: {
                slidesPerView: 1,
              },
              1024: {
                slidesPerView: 2,
              },
              1280: {
                slidesPerView: 3,
              },
            }}
          >
            {formatList.map((item, index) => (
              <SwiperSlide key={index}>
                <video
                  src={typeof item.bgVideo === "string" ? item.bgVideo : item.bgVideo?.url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="object-cover w-full file-mask-xl-video relative max-h-[350px]"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        <div className="flex justify-center items-center gap-4 w-full xl:hidden">
          <div className="flex gap-x-2">
            <button
              onClick={handlePrevClick}
              className={`p-2 border rounded-full text-gray-400 hover:border-white hover:text-white ${backgroundColorClass}`}
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={handleNextClick}
              className={`p-2 border rounded-full text-gray-400 hover:border-white hover:text-white ${backgroundColorClass}`}
            >
              <ArrowRightIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="h-1 w-full bg-neutral-200 rounded-3xl">
            <div
              className={`h-1 ${backgroundProggressColorClass} rounded-3xl`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}
